import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from './Loader';

const StartupForm = ({ onClose, goBack, initialStartupName }) => {
  const initialFormData = {
    name: '',
    industry: '',
    email: '',
    idea: '',
    address: '',
    contactNumber: '',
    password: '',
    confirmPassword: '',
    startUpDescription: '',
    members: [
      { firstName: '', surname: '' },
      { firstName: '', surname: '' },
    ],
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isFinalized, setIsFinalized] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contactNumber') {
      // 1. Remove anything that isn't a number
      const numericValue = value.replace(/\D/g, '');
      // 2. Only update if it's 11 digits or fewer
      if (numericValue.length <= 11) {
        setFormData({ ...formData, [name]: numericValue });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMemberChange = (index, e) => {
    const { name, value } = e.target;
    const updatedMembers = [...formData.members];
    updatedMembers[index][name] = value;
    setFormData({ ...formData, members: updatedMembers });
  };

  const addMember = () => {
    setFormData({
      ...formData,
      members: [...formData.members, { firstName: '', surname: '' }],
    });
  };

  const removeMember = (index) => {
    if (formData.members.length > 3) {
      const updatedMembers = [...formData.members];
      updatedMembers.splice(index, 1);
      setFormData({ ...formData, members: updatedMembers });
    } else {
      toast.error('At least 3 members are required.');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (formData.name.length > 20) errors.name = 'Startup name must be 20 characters or less.';
    if (formData.email.length > 30) errors.email = 'Email must be 30 characters or less.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email address.';
    if (formData.idea.length > 800) errors.idea = 'Idea must be 800 characters or less.';
    if (formData.contactNumber.length !== 11) {
      errors.contactNumber = 'Contact number must be exactly 11 digits.';
    }

    return errors;
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) {
      toast.error(Object.values(errors).join('\n'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`https://tbido-website-api.azurewebsites.net/postApplication/${initialStartupName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName: initialStartupName,
          startUpName: formData.name,
          emailAddress: formData.email,
          contactNumber: formData.contactNumber,
          address: formData.address,
          startUpDescription: formData.startUpDescription,
          members: formData.members,
          password: formData.password,
        }),
      });

      if (res.ok) {
        setIsFinalized(true);
        toast.success('Application successful! Click "Submit" to complete.');
      } else {
        toast.error('Application already exists.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeApplication = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://tbido-website-api.azurewebsites.net/finalizeApplication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName: initialStartupName,
          emailAddress: formData.email,
          password: formData.password,
        }),
      });

      if (res.ok) {
        setFormData(initialFormData);
        setIsFinalized(false);
        toast.success('Check your email for tracking credentials.');
        setTimeout(onClose, 5000);
      } else {
        toast.error('Submission failed.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://tbido-website-api.azurewebsites.net/clearApplication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName: initialStartupName,
          emailAddress: formData.email,
          contactNumber: formData.contactNumber,
        }),
      });

      if (res.ok) {
        setFormData(initialFormData);
        setIsFinalized(false);
        toast.success('Application cleared.');
      } else {
        toast.error('Failed to clear.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to clear.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 m-0">
      <div className="w-full max-w-4xl h-[75vh] bg-white rounded-lg shadow-lg flex overflow-hidden">
        {/* Left Side Image */}
        <div className="w-1/3.5 hidden sm:block p-4">
          <img
            src="/images/startup.png"
            alt="Startup"
            className="h-full w-full object-cover rounded-l-lg"
          />
        </div>

        {/* Right Side Form with Scroll */}
        <form
          onSubmit={(e) => e.preventDefault()}
          className="w-full sm:w-2/3 p-6 overflow-y-auto"
        >
          <div className="flex justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-500">Startup Application Form</h2>
            <button onClick={onClose} className="text-xl text-gray-500 hover:text-gray-700">&times;</button>
          </div>

          {loading && <Loader />}
          <p className="text-sm mb-4 text-gray-500">Event Name: <b>{initialStartupName}</b></p>

          {/* Fields */}
          <label className="font-semibold text-gray-500">Startup Name:</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isFinalized}
            className="mb-3 w-full border-none rounded p-2 bg-gray-100"
          />

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-semibold text-gray-500">Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isFinalized}
                className="w-full border-none rounded p-2 bg-gray-100"
              />
            </div>
            <div>
              <label className="font-semibold text-gray-500">Contact Number:</label>
              <input
                type="tel" // Suggests a telephone keypad on mobile
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                maxLength="11" // Prevents typing more than 11 digits
                placeholder="09123456789"
                disabled={isFinalized}
                className="w-full border-none rounded p-2 bg-gray-100"
              />
            </div>
          </div>

          {/*Password Fields*/}
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-semibold text-gray-500">Password:</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                disabled={isFinalized}
                className="w-full border-none rounded p-2 bg-gray-100"
              />
            </div>
            <div>
              <label className="font-semibold text-gray-500">Confirm Password:</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isFinalized}
                className="w-full border-none rounded p-2 bg-gray-100"
              />
            </div>
          </div>

          <label className="font-semibold text-gray-500">Address:</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            disabled={isFinalized}
            className="mb-4 w-full border-none rounded p-2 bg-gray-100"
          />

          {/* Members */}
          <label className="font-semibold text-gray-500">Members:</label>
          {formData.members.map((member, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={member.firstName}
                onChange={(e) => handleMemberChange(index, e)}
                className="w-1/2 border-none rounded p-2 bg-gray-100"
                disabled={isFinalized}
              />
              <input
                type="text"
                name="surname"
                placeholder="Surname"
                value={member.surname}
                onChange={(e) => handleMemberChange(index, e)}
                className="w-1/2 border-none rounded p-2 bg-gray-100"
                disabled={isFinalized}
              />
              {!isFinalized && formData.members.length > 3 && (
                <button
                  type="button"
                  onClick={() => removeMember(index)}
                  className="text-red-600 font-bold"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {!isFinalized && (
            <button type="button" onClick={addMember} className="mb-4 text-black-600 font-semibold">
              + Add Member
            </button>
          )}
          <br />
          {/* Description */}
          <label className="font-semibold text-gray-500">Description:</label>
          <textarea
            name="startUpDescription"
            rows="4"
            maxLength="500"
            value={formData.startUpDescription}
            onChange={handleChange}
            disabled={isFinalized}
            className="mb-4 w-full border-none rounded p-2 bg-gray-100"
            placeholder="Describe your startup..."
          />

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-end gap-4">
            <button onClick={goBack} className="rounded-full px-5 py-1.5 border border-gray-400 text-gray-700 hover:bg-gray-200">Back to Events</button>

            {!isFinalized ? (
              <button onClick={handleSubmitApplication} className="rounded-full bg-[linear-gradient(45deg,#800000,#000080)] text-white rounded px-4 py-2">
                Finalize
              </button>
            ) : (
              <>
                <button onClick={handleFinalizeApplication} className="bg-green-600 text-white rounded px-4 py-2">
                  Submit
                </button>
                <button onClick={handleClear} className="bg-red-600 text-white rounded px-4 py-2">
                  Clear
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartupForm;